'use client'

import { useState, useCallback } from 'react'
import { usePathname } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { HeaderLogo } from '@/components/logo'
import { MainNav } from '@/components/main-nav'
import { MobileNavLinks } from '@/components/mobile-nav-links'
import CartDrawer from '@/components/cart-drawer'
import { Menu, X, ShoppingCart } from 'lucide-react'
import { useCartUI } from '@/context/cart-ui-context'
import { useCart } from '@/context/cart-context'
import { Truck, CreditCard } from 'lucide-react'

export function SiteHeader() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const pathname = usePathname()

  const isProductPage = pathname?.startsWith('/productos/')
  const { toggleCart } = useCartUI()
  const { itemCount } = useCart()

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev)
  }, [])

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false)
  }, [])

  return (
    <header
      className={`
        sticky top-0 z-50 w-full
        ${isProductPage ? 'bg-white' : 'bg-kuma-beige'}
      `}
    >
      {/* 🚚 Banner envío gratis + cuotas */}
      <div className='w-full bg-[#9E9284] py-1 md:py-2 text-center text-[10px] md:text-sm font-regular text-[#F4F6EF] leading-tight'>
        <Truck size={11} className='inline-block mr-1 mb-[1px]' />
        Envío gratis desde $100.000
        <span className='mx-1'>·</span>
        <CreditCard size={11} className='inline-block mr-1 mb-[1px]' />3 cuotas
        sin interés
      </div>

      <div className={`flex items-center h-[64px] md:h-[72px]`}>
        <div className='max-w-7xl mx-auto w-full px-4'>
          {/* Desktop */}
          <div className='hidden lg:flex items-center justify-between h-[72px]'>
            <HeaderLogo />

            <MainNav />

            <div className='flex items-center space-x-4'>
              <div className='relative w-[32px] h-[32px] flex items-center justify-center'>
                <Button
                  variant='icon'
                  size='icon'
                  className='relative'
                  aria-label='Abrir carrito'
                  onClick={toggleCart}
                >
                  <ShoppingCart className='h-4 w-4 lg:h-5 lg:w-5' />
                  {itemCount > 0 && (
                    <span className='absolute -top-1 -right-1 lg:-top-2 lg:-right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 lg:w-5 lg:h-5 flex items-center justify-center'>
                      {itemCount}
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Mobile */}
          <div className='lg:hidden flex items-center justify-between h-[64px]'>
            <Button
              variant='ghost'
              size='icon'
              className='h-9 w-9 rounded-lg'
              aria-label='Toggle menu'
              onClick={toggleMobileMenu}
            >
              {isMobileMenuOpen ? (
                <X className='h-5 w-5 text-[#7D744F]' />
              ) : (
                <Menu className='h-5 w-5 text-[#7D744F]' />
              )}
            </Button>

            <HeaderLogo />

            <div className='relative w-[32px] h-[32px] flex items-center justify-center'>
              <Button
                variant='icon'
                size='icon'
                className='relative'
                aria-label='Abrir carrito'
                onClick={toggleCart}
              >
                <ShoppingCart className='h-4 w-4 lg:h-5 lg:w-5' />
                {itemCount > 0 && (
                  <span className='absolute -top-1 -right-1 lg:-top-2 lg:-right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 lg:w-5 lg:h-5 flex items-center justify-center'>
                    {itemCount}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <>
          <div
            className='fixed inset-0 bg-black/50 z-40 lg:hidden'
            onClick={closeMobileMenu}
          />

          <div className='fixed inset-y-0 left-0 w-80 max-w-[85vw] bg-kuma-beige z-50 transform transition-transform duration-300 ease-in-out'>
            <div className='flex items-center justify-between p-4 border-b border-gray-200'>
              <h2 className='text-lg font-allround font-medium text-[#2B2B2B]'>
                Menú
              </h2>
              <Button
                variant='ghost'
                size='icon'
                onClick={closeMobileMenu}
                aria-label='Cerrar menú'
                className='h-8 w-8'
              >
                <X className='h-4 w-4 text-[#7D744F]' />
              </Button>
            </div>

            <div className='flex-1 overflow-y-auto py-4'>
              <MobileNavLinks onClick={closeMobileMenu} />
            </div>
          </div>
        </>
      )}

      <CartDrawer />
    </header>
  )
}
